import { ExpandMore, InfoOutlined } from '@mui/icons-material';
import {
  AccordionDetails,
  AccordionSummary,
  Accordion as MuiAccordion,
  ClickAwayListener,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
} from '@mui/material';
import { useState } from 'react';
import FavoriteControl from './FavoriteControl';
import useBreakpoints from '../../hooks/useBreakpoints';
import { BetDetails } from '../atoms/BetDetails';
import BetBuilderBadge from '../atoms/BetBuilderBadge';
import useMobileOnHold from '../../hooks/useMobileOnHold';
import { useSkinStyles } from '../../hooks/useSkinStyles';
import { MARKET_ACCORDION_SKIN_STYLES } from '../../styles/components/marketAccordion';
import useLocalization from '../../hooks/useLocalization';
import { mergeSx } from '../../utils/styles';

type AccordionProps = {
  title: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  marketId?: string;
  isFavoriteMarket?: boolean;
  showFavorites?: boolean;
  marketTypeName?: string;
  description?: string | null;
  showBetBuilderBadge?: boolean;
};

const MarketAccordion = ({
  title,
  children,
  defaultExpanded,
  marketId,
  isFavoriteMarket,
  showFavorites,
  marketTypeName,
  description,
  showBetBuilderBadge,
}: AccordionProps) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [hovered, setHovered] = useState(false);
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false);

  const styles = useSkinStyles(MARKET_ACCORDION_SKIN_STYLES);
  const { isMobile } = useBreakpoints();
  const { t } = useLocalization();
  const { holdHandlers, isChecked, handleFavorite, isPending } = useMobileOnHold(
    !!isFavoriteMarket,
    'market-types',
    marketId
  );
  const normalizedDescription = description?.trim();
  const handleExpand = () => {
    setExpanded(!expanded);
  };
  const handleDescriptionClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (isMobile) {
      setIsDescriptionOpen((isOpen) => !isOpen);
    }
  };
  const handleDescriptionKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (event.key === 'Escape') {
      setIsDescriptionOpen(false);
    }
  };
  const canShowFavorite = Boolean(showFavorites && marketId);

  if (!title) return <Skeleton variant='rectangular' width='100%' height={40} animation='wave' />;

  return (
    <MuiAccordion
      sx={styles.accordion}
      expanded={expanded}
      onMouseEnter={() => !isMobile && setHovered(true)}
      onMouseLeave={() => !isMobile && setHovered(false)}
      onClick={handleExpand}
    >
      <AccordionSummary
        expandIcon={<ExpandMore onClick={handleExpand} sx={styles.expandMoreIcon} />}
        aria-controls='markets-content'
        id='markets-header'
        sx={styles.accordionSummary}
        {...holdHandlers}
        onContextMenu={(e) => e.preventDefault()}
        onClick={(e) => e.stopPropagation()}
      >
        <Stack direction='row' sx={mergeSx(styles.marketTitleContainer, { alignItems: 'center' })}>
          <Stack direction='row' sx={mergeSx(styles.marketLabelContainer, { alignItems: 'center' })}>
            <BetDetails.Market marketName={title} marketType={marketTypeName} sx={styles.marketName} />
          </Stack>
          {showBetBuilderBadge && <BetBuilderBadge sx={styles.betBuilderBadge} />}
        </Stack>
        <FavoriteControl
          isChecked={!!isChecked}
          canShow={canShowFavorite}
          disabled={isPending}
          isHovered={hovered}
          visibilityMode='skin-config'
          containerSx={styles.checkboxContainer}
          uncheckedColor='neutral.400'
          variant='small'
          onClick={(e) => e.stopPropagation()}
          onChange={handleFavorite}
        />
        {normalizedDescription && (
          <ClickAwayListener onClickAway={() => setIsDescriptionOpen(false)}>
            <Tooltip
              arrow
              title={normalizedDescription}
              placement='top'
              open={isDescriptionOpen}
              onOpen={() => setIsDescriptionOpen(true)}
              onClose={() => setIsDescriptionOpen(false)}
              disableFocusListener={isMobile}
              disableHoverListener={isMobile}
              disableTouchListener
              slotProps={{
                tooltip: {
                  sx: styles.descriptionTooltip,
                },
              }}
            >
              <IconButton
                aria-label={t('info')}
                size='small'
                sx={styles.descriptionButton}
                onClick={handleDescriptionClick}
                onKeyDown={handleDescriptionKeyDown}
                onPointerDown={(event) => event.stopPropagation()}
                onTouchStart={(event) => event.stopPropagation()}
              >
                <InfoOutlined sx={styles.descriptionIcon} />
              </IconButton>
            </Tooltip>
          </ClickAwayListener>
        )}
      </AccordionSummary>
      <AccordionDetails sx={styles.accordionDetails}>{children}</AccordionDetails>
    </MuiAccordion>
  );
};

export default MarketAccordion;
